import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportClientDiagnosticError } from '../lib/telemetry';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  copied: boolean;
}

declare global {
  interface Window {
    __DARK_TEL_CRM_ERRORS__?: Array<{
      id: string;
      timestamp: string;
      url: string;
      error: string;
      stack?: string;
      componentStack?: string;
    }>;
  }
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: null,
    copied: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'err-' + Date.now();
    return { hasError: true, error, errorInfo: null, errorId };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'err-' + Date.now());
    this.setState({ error, errorInfo, errorId });
    console.error(`[DARK_TEL_CRM_ERROR ${errorId}]:`, error, errorInfo);

    // Ephemeral in-memory ring buffer (up to 10 exceptions)
    if (typeof window !== 'undefined') {
      window.__DARK_TEL_CRM_ERRORS__ = window.__DARK_TEL_CRM_ERRORS__ || [];
      window.__DARK_TEL_CRM_ERRORS__.unshift({
        id: errorId,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        error: error?.message || String(error),
        stack: error?.stack,
        componentStack: errorInfo?.componentStack || undefined
      });
      if (window.__DARK_TEL_CRM_ERRORS__.length > 10) {
        window.__DARK_TEL_CRM_ERRORS__.pop();
      }
    }

    reportClientDiagnosticError({
      service: 'crm',
      errorName: error?.name || 'ReactCrash',
      message: error?.message || String(error),
      stack: error?.stack,
      level: 'error',
      customDetails: {
        componentStack: errorInfo?.componentStack,
        referenceId: errorId,
      }
    });
  }

  private handleCopyDiagnosticReport = () => {
    const { error, errorInfo, errorId } = this.state;
    const report = [
      `### 🚨 CRM Tenant Crash Report [ID: \`${errorId}\`]`,
      `- **Timestamp**: ${new Date().toISOString()} (UTC)`,
      `- **URL**: ${typeof window !== 'undefined' ? window.location.href : 'Unknown'}`,
      `- **User Agent**: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'}`,
      `- **Error Type**: ${error?.name || 'Error'}`,
      `\n#### Exception Message\n\`\`\`\n${error?.message || String(error)}\n\`\`\``,
      error?.stack ? `\n#### Error Stack\n\`\`\`\n${error.stack}\n\`\`\`` : '',
      errorInfo?.componentStack ? `\n#### Component Tree\n\`\`\`\n${errorInfo.componentStack}\n\`\`\`` : '',
      `\n--- \n*Generated securely by DarkTel CRM Boundary. Ephemeral memory only.*`
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(report);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 3000);
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '3rem 1.5rem', maxWidth: '850px', margin: '0 auto', color: 'white', fontFamily: 'sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span style={{
              background: '#ef4444',
              color: 'white',
              fontSize: '0.8rem',
              fontWeight: 700,
              padding: '0.2rem 0.6rem',
              borderRadius: '4px'
            }}>
              CRM RENDER CRASH
            </span>
            <span style={{ color: 'var(--text-muted, #9ca3af)', fontFamily: 'monospace', fontSize: '0.85rem' }}>
              Ref: {this.state.errorId}
            </span>
          </div>

          <h1 style={{ color: '#ef4444', marginBottom: '0.75rem', fontSize: '2rem' }}>Tenant Portal Runtime Exception</h1>
          <p style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: '1.5rem', fontSize: '1rem', lineHeight: '1.5' }}>
            A runtime exception occurred while rendering the tenant portal. The error has been captured securely.
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <button 
              onClick={this.handleCopyDiagnosticReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.65rem 1.25rem',
                background: this.state.copied ? '#10b981' : 'var(--primary, #3b82f6)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'background 0.2s ease'
              }}
            >
              {this.state.copied ? '✓ Copied AI Diagnostic Report!' : '📋 Copy Diagnostic Report for AI'}
            </button>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '0.65rem 1.25rem',
                background: 'rgba(255,255,255,0.08)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              ↻ Reload Portal
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.65rem 1.25rem',
                background: 'transparent',
                color: 'var(--text-muted, #9ca3af)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Return to Login
            </button>
          </div>
          
          <div className="glass-panel" style={{ padding: '1.25rem', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', marginBottom: '1.25rem', overflowX: 'auto', borderRadius: '8px' }}>
             <h3 style={{ marginTop: 0, color: '#fca5a5', marginBottom: '0.75rem', fontSize: '1rem' }}>Exception Details</h3>
             <pre style={{ margin: 0, fontFamily: 'monospace', color: '#fee2e2', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {this.state.error && this.state.error.toString()}
             </pre>
          </div>

          {this.state.errorInfo && (
            <div className="glass-panel" style={{ padding: '1.25rem', marginBottom: '1.5rem', overflowX: 'auto', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
               <h3 style={{ marginTop: 0, color: 'white', marginBottom: '0.75rem', fontSize: '1rem' }}>Component Stack Trace</h3>
               <pre style={{ margin: 0, fontFamily: 'monospace', color: 'var(--text-muted, #9ca3af)', fontSize: '0.8rem', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>
                  {this.state.errorInfo.componentStack}
               </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
